import {
  infiniteQueryOptions,
  queryOptions,
  type InfiniteData,
  type Query,
  type QueryClient,
} from "@tanstack/react-query";
import type { IntakeService } from "@/services/intake.service";
import type { TIntakeForm, TIntakeRequestDraft, TWorkflowRenderSurface } from "@/types/intake";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The intake workflows (forms) reads (NG-2808), Query-only since NG-2985:
 *
 * - **page** `[slug, uid, 'request-forms', 'page', params]`: one cursor page of
 *   forms per fetch, keyed by search, sort, active filter, draft overlay and
 *   team. The settings Intake Workflows table and the requester's form picker.
 *   Mutable working data.
 * - **catalogue** `[slug, uid, 'request-forms', 'all']`: every form with its
 *   draft overlay, for the requests list's Form filter. Mutable.
 * - **one** `[slug, uid, 'request-forms', 'one', id]`: a form the requester
 *   deep-linked to, live shape (never the draft). Access-sensitive: a form the
 *   caller may no longer submit to must not be served from a copy.
 * - **builder** `[slug, uid, 'request-forms', 'builder', id]`: the workflow the
 *   builder opens, with its draft overlay and the caller's `can_edit`, which
 *   only this read answers (a list row never evaluates it, #3144 D7).
 *   Access-sensitive. The builder route re-reads it on every open
 *   (`useBuilderForm`), because editability turns over with the workflow's
 *   managing team; the builder's inner surfaces share that read. It seeds the
 *   builder's draft once per open (NG-3051).
 * - **surface** `[slug, uid, 'request-forms', 'surface', id]`: the form's
 *   published workflow render surface; a form with none 404s. Reference data:
 *   it changes only when a workflow is published.
 * - **drafts** `[slug, uid, 'request-drafts', 'mine']`: the caller's private
 *   draft requests. Mutable.
 *
 * Every read is a plain GET, so all declare `"safe-read"`.
 *
 * @writers The forms table's active switch and delete patch the cached pages
 * (`putCachedForm`, `removeCachedForm`), re-reading a page or catalogue read
 * in flight, whose answer could undo the patch. Every `IntakeStore` form writer
 * (create, save, publish, discard, active switch, delete, import) marks the
 * family stale (`markCachedFormsStale`), so the next reader sees it. The
 * requester's draft save, submit and delete patch or mark the drafts stale
 * (`dropCachedDraft`, `markCachedDraftsStale`). The builder entry is not
 * marked stale by a form write: each writer puts its echo there instead
 * (`putCachedBuilderForm`, `removeCachedBuilderForm`), so an autosave never
 * re-reads the workflow under the author.
 */

/** A page's parameters: everything but the cursor, which the page walk owns. */
export interface FormsPageParams {
  q?: string;
  per_page: number;
  order_by?: string;
  is_active?: boolean;
  active_only?: boolean;
  includeDraft?: boolean;
  team?: string;
}

export interface FormsPage {
  results: TIntakeForm[];
  next_cursor: string;
  next_page_results: boolean;
  total_count: number;
}

const FORMS = "request-forms";

/** The page key. */
function formsPageKey(scope: QueryScope, params: FormsPageParams) {
  return scopedKey(scope, FORMS, "page", params);
}

/** The catalogue key. */
function formsCatalogueKey(scope: QueryScope) {
  return scopedKey(scope, FORMS, "all");
}

function formKey(scope: QueryScope, formId: string) {
  return scopedKey(scope, FORMS, "one", formId);
}

function builderFormKey(scope: QueryScope, formId: string) {
  return scopedKey(scope, FORMS, "builder", formId);
}

function surfaceKey(scope: QueryScope, formId: string) {
  return scopedKey(scope, FORMS, "surface", formId);
}

const DRAFTS = "request-drafts";

function draftsKey(scope: QueryScope) {
  return scopedKey(scope, DRAFTS, "mine");
}

/** One form, live shape, for the requester who deep-linked to it. */
export function requesterFormQuery(
  service: Pick<IntakeService, "getForm">,
  scope: QueryScope,
  formId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: formKey(scope, formId),
    queryFn: scopedRead<TIntakeForm>(
      (request) => service.getForm(scope.tenant, formId, {}, request),
      { retry: "safe-read" },
    ),
  });
}

/** The workflow the builder opens: its draft overlay, and the caller's `can_edit`. */
export function builderFormQuery(
  service: Pick<IntakeService, "getForm">,
  scope: QueryScope,
  formId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: builderFormKey(scope, formId),
    queryFn: scopedRead<TIntakeForm>(
      (request) => service.getForm(scope.tenant, formId, { includeDraft: true }, request),
      { retry: "safe-read" },
    ),
  });
}

/**
 * The form's published workflow render surface. A form with no published
 * workflow 404s and the requester falls back to its legacy definition, so a
 * 404 is an answer, not a fault; so is a 400 (a snapshot that cannot run),
 * which falls back the same way.
 */
export function workflowSurfaceQuery(
  service: Pick<IntakeService, "renderWorkflowSurface">,
  scope: QueryScope,
  formId: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    meta: { ...FRESHNESS.reference.meta, expectedStatuses: [400, 404] },
    queryKey: surfaceKey(scope, formId),
    queryFn: scopedRead<TWorkflowRenderSurface>(
      (request) => service.renderWorkflowSurface(scope.tenant, formId, request),
      { retry: "safe-read" },
    ),
  });
}

/** The caller's private draft requests. */
export function requestDraftsQuery(
  service: Pick<IntakeService, "listRequestDrafts">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: draftsKey(scope),
    queryFn: scopedRead<TIntakeRequestDraft[]>(
      (request) => service.listRequestDrafts(scope.tenant, undefined, request),
      { retry: "safe-read" },
    ),
  });
}

/** Forms one cursor page at a time. */
export function formsPageQuery(
  service: Pick<IntakeService, "listFormsPage">,
  scope: QueryScope,
  params: FormsPageParams,
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: formsPageKey(scope, params),
    initialPageParam: null as string | null,
    queryFn: scopedRead<FormsPage, string | null>(
      (request, { pageParam }) =>
        service.listFormsPage(scope.tenant, { ...params, cursor: pageParam }, request),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: FormsPage, _all, cursor) =>
      last.next_page_results && last.next_cursor && last.next_cursor !== cursor
        ? last.next_cursor
        : undefined,
  });
}

/** Every form, with its draft overlay. */
export function formsCatalogueQuery(service: Pick<IntakeService, "listForms">, scope: QueryScope) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: formsCatalogueKey(scope),
    queryFn: scopedRead<TIntakeForm[]>(
      (request) => service.listForms(scope.tenant, { includeDraft: true }, request),
      { retry: "safe-read" },
    ),
  });
}

/** The rows of every loaded page, first page first, each form once. */
export function formPageRows(data: InfiniteData<FormsPage> | undefined): TIntakeForm[] {
  const seen = new Set<string>();
  const rows: TIntakeForm[] = [];
  for (const page of data?.pages ?? []) {
    for (const form of page.results) {
      if (seen.has(form.id)) continue;
      seen.add(form.id);
      rows.push(form);
    }
  }
  return rows;
}

// ---- Writers ------------------------------------------------------------

type Pages = InfiniteData<FormsPage, string | null>;

function isFormsQuery(tenant: string, query: Query): boolean {
  return query.queryKey[0] === tenant && query.queryKey[2] === FORMS;
}

function isPageQuery(query: Query): boolean {
  return query.queryKey[3] === "page";
}

/** A page or the catalogue: the entries that hold form rows. */
function isFormRowsQuery(tenant: string, query: Query): boolean {
  return isFormsQuery(tenant, query) && (isPageQuery(query) || query.queryKey[3] === "all");
}

function rewrite<T>(client: QueryClient, query: Query, update: (current: T) => T): void {
  const current = query.state.data as T | undefined;
  if (current === undefined) return;
  client.setQueryData<T>(query.queryKey, update(current), {
    updatedAt: query.state.dataUpdatedAt,
  });
}

/** A form as any cached page or the catalogue holds it, to seed the builder. */
export function findCachedForm(
  client: QueryClient,
  tenant: string,
  formId: string,
): TIntakeForm | undefined {
  for (const query of client.getQueryCache().findAll({
    predicate: (q) => isFormRowsQuery(tenant, q),
  })) {
    const data = query.state.data as Pages | TIntakeForm[] | undefined;
    const rows = Array.isArray(data) ? data : (data?.pages.flatMap((p) => p.results) ?? []);
    const form = rows.find((f) => f.id === formId);
    if (form) return form;
  }
  return undefined;
}

/**
 * Make again a forms read that is in flight while a write patches its entry.
 * It may have started before the write, and its answer would undo the patch;
 * the new read cancels it (NG-3196). A read with nothing in flight is left
 * alone, so a patch alone sends no request.
 */
function rereadInFlight(
  client: QueryClient,
  query: Query,
  context: { tenant: string; formId: string; write: "switch" | "delete" },
): void {
  if (query.state.fetchStatus !== "fetching") return;
  client
    .invalidateQueries({ queryKey: query.queryKey, exact: true, refetchType: "all" })
    .catch((error: unknown) => {
      console.warn("[request-forms] re-reading the forms after a write failed", {
        ...context,
        error,
      });
    });
}

/**
 * Put `form` in place of the cached row with its id, in every page and the
 * catalogue, re-reading any of them in flight (NG-3197).
 */
export function putCachedForm(client: QueryClient, tenant: string, form: TIntakeForm): void {
  for (const query of client.getQueryCache().findAll({
    predicate: (q) => isFormRowsQuery(tenant, q),
  })) {
    if (isPageQuery(query)) {
      rewrite<Pages>(client, query, (pages) => ({
        ...pages,
        pages: pages.pages.map((page) => ({
          ...page,
          results: page.results.map((f) => (f.id === form.id ? form : f)),
        })),
      }));
    } else {
      rewrite<TIntakeForm[]>(client, query, (rows) =>
        rows.map((f) => (f.id === form.id ? form : f)),
      );
    }
    rereadInFlight(client, query, { tenant, formId: form.id, write: "switch" });
  }
}

/**
 * Drop a deleted form from every cached page (with its count) and the
 * catalogue, re-reading any of them in flight: an answer from before the
 * delete would write the row back (NG-3196).
 */
export function removeCachedForm(client: QueryClient, tenant: string, formId: string): void {
  for (const query of client.getQueryCache().findAll({
    predicate: (q) => isFormRowsQuery(tenant, q),
  })) {
    if (isPageQuery(query)) {
      rewrite<Pages>(client, query, (pages) => {
        if (!pages.pages.some((page) => page.results.some((f) => f.id === formId))) return pages;
        return {
          ...pages,
          pages: pages.pages.map((page) => ({
            ...page,
            results: page.results.filter((f) => f.id !== formId),
            total_count: Math.max(0, page.total_count - 1),
          })),
        };
      });
    } else {
      rewrite<TIntakeForm[]>(client, query, (rows) => rows.filter((f) => f.id !== formId));
    }
    rereadInFlight(client, query, { tenant, formId, write: "delete" });
  }
}

function isBuilderFormQuery(tenant: string, formId: string, query: Query): boolean {
  return (
    isFormsQuery(tenant, query) && query.queryKey[3] === "builder" && query.queryKey[4] === formId
  );
}

/**
 * Put a form write's echo in the builder's entry for it, under every
 * principal. An echo never evaluates `can_edit` (only the detail read does),
 * so the entry keeps the answer it holds.
 */
export function putCachedBuilderForm(client: QueryClient, tenant: string, form: TIntakeForm): void {
  for (const query of client.getQueryCache().findAll({
    predicate: (q) => isBuilderFormQuery(tenant, form.id, q),
  })) {
    rewrite<TIntakeForm>(client, query, (current) =>
      typeof form.can_edit === "boolean" ? form : { ...form, can_edit: current.can_edit },
    );
  }
}

/** Drop a deleted form's builder entry. */
export function removeCachedBuilderForm(client: QueryClient, tenant: string, formId: string): void {
  client.removeQueries({ predicate: (q) => isBuilderFormQuery(tenant, formId, q) });
}

/**
 * Mark every cached forms read of `tenant` stale after a form write: shown
 * reads re-read now, the rest on their next mount. A write can move a row out
 * of a filtered or sorted page, so patching alone is not enough. The builder
 * entry is left alone: its writer puts the echo there (`putCachedBuilderForm`).
 */
export function markCachedFormsStale(client: QueryClient, tenant: string): void {
  client
    .invalidateQueries({
      predicate: (q) => isFormsQuery(tenant, q) && q.queryKey[3] !== "builder",
    })
    .catch((error: unknown) => {
      console.warn("[request-forms] marking the forms stale failed", { tenant, error });
    });
}

/** Drop a deleted draft from the cached drafts. */
export function dropCachedDraft(client: QueryClient, tenant: string, draftId: string): void {
  for (const query of client.getQueryCache().findAll({
    predicate: (q) => q.queryKey[0] === tenant && q.queryKey[2] === DRAFTS,
  })) {
    rewrite<TIntakeRequestDraft[]>(client, query, (rows) => rows.filter((d) => d.id !== draftId));
  }
}

/** A draft was saved or submitted: read the drafts again when next shown. */
export function markCachedDraftsStale(client: QueryClient, tenant: string): void {
  client
    .invalidateQueries({ predicate: (q) => q.queryKey[0] === tenant && q.queryKey[2] === DRAFTS })
    .catch((error: unknown) => {
      console.warn("[request-forms] marking the drafts stale failed", { tenant, error });
    });
}
